import { useTranslation } from 'react-i18next';

import type { GateAsset } from '@/hooks/useReviewGate';
import { cn } from '@/lib/utils';

/** Per-asset producer decision: flagged for action, with an optional reason. */
export interface Mark {
  on: boolean;
  note: string;
}

export type Marks = Record<string, Mark>;

/**
 * Three gates — footage, reframe and voice — are the same interaction: review each
 * asset, flag the ones that need action, optionally say why. Only the media element
 * and the wording differ, so they share this component rather than three copies.
 */
export default function FlaggableAssetGate({
  assets,
  marks,
  onChange,
  hint,
  onLabel,
  offLabel,
  notePlaceholder,
  columns = 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4',
  accent = 'amber',
  renderMedia,
  /** Whether the whole tile toggles (footage) or just the label row (reframe/voice). */
  toggleOnMedia = false,
}: {
  assets: GateAsset[];
  marks: Marks;
  onChange: (marks: Marks) => void;
  hint: string;
  onLabel: string;
  offLabel: string;
  notePlaceholder: string;
  columns?: string;
  accent?: 'amber' | 'red';
  renderMedia: (asset: GateAsset) => React.ReactNode;
  toggleOnMedia?: boolean;
}) {
  const { t } = useTranslation('production');
  const toggle = (id: string) => onChange({ ...marks, [id]: { on: !marks[id]?.on, note: marks[id]?.note ?? '' } });
  const setNote = (id: string, note: string) => onChange({ ...marks, [id]: { on: marks[id]?.on ?? true, note } });

  const activeBorder = accent === 'red' ? 'border-red-500 bg-red-500/5' : 'border-amber-500 bg-amber-500/5';
  const activeChip = accent === 'red' ? 'bg-red-500 text-white' : 'bg-amber-500 text-black';

  return (
    <div className="mt-3 space-y-2 border-t border-primary/25 pt-3">
      <p className="m-0 text-xs leading-relaxed text-muted-foreground">{hint}</p>
      <div className={cn('grid gap-2', columns)}>
        {assets.map((a) => {
          const on = !!marks[a.id]?.on;
          return (
            <div
              key={a.id}
              className={cn(
                'overflow-hidden rounded-lg border-2 transition-all',
                on ? activeBorder : 'border-transparent bg-background/40',
              )}
            >
              {toggleOnMedia ? (
                <button
                  type="button"
                  onClick={() => toggle(a.id)}
                  className="relative block w-full"
                  title={on ? t('gate.keepThis') : t('gate.markAs', { label: onLabel })}
                >
                  {renderMedia(a)}
                  <span className="absolute right-1 top-1 rounded bg-black/70 px-1 py-0.5 text-[9px] font-bold text-white">
                    {on ? onLabel : offLabel}
                  </span>
                </button>
              ) : (
                renderMedia(a)
              )}

              <button
                type="button"
                onClick={() => toggle(a.id)}
                className="flex w-full items-center justify-between gap-2 px-1.5 py-1 text-left"
                title={on ? t('gate.keepThis') : t('gate.markAs', { label: onLabel })}
              >
                <span className="truncate text-[10px] font-medium" title={a.id}>
                  {a.speaker ? `${a.speaker} — ` : ''}
                  {a.id}
                </span>
                {!toggleOnMedia && (
                  <span
                    className={cn(
                      'shrink-0 rounded px-1 py-0.5 text-[9px] font-bold',
                      on ? activeChip : 'bg-black/60 text-white',
                    )}
                  >
                    {on ? onLabel : offLabel}
                  </span>
                )}
              </button>

              {on && (
                <input
                  value={marks[a.id]?.note ?? ''}
                  onChange={(e) => setNote(a.id, e.target.value)}
                  placeholder={notePlaceholder}
                  className="mx-1.5 mb-1.5 w-[calc(100%-0.75rem)] rounded border border-border bg-background
                             px-1.5 py-1 text-[10px] outline-none focus:border-primary"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
